import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Package, Store, Tag, TrendingUp, TrendingDown, AlertCircle, ShoppingBag, MessageCircle, Wallet, Receipt } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api`;

const STATUS_COLORS = { finalizate: "#10b981", noi: "#3b82f6", anulate: "#ef4444" };
const PIE_COLORS = ["#f97316", "#0ea5e9", "#10b981", "#a855f7", "#ec4899", "#facc15", "#6366f1"];

function KpiCard({ icon: Icon, label, value, subtitle, accent = "text-zinc-200", testId, onClick }) {
  const interactive = typeof onClick === "function";
  const Tag = interactive ? "button" : "div";
  return (
    <Tag
      data-testid={testId}
      onClick={onClick}
      type={interactive ? "button" : undefined}
      className={
        "w-full rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4 text-left shadow-lg transition " +
        (interactive
          ? "cursor-pointer hover:border-orange-500/30 hover:bg-zinc-900/70 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
          : "")
      }
    >
      <div className="flex items-start justify-between">
        <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">{label}</p>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <p className={`mt-2 text-2xl font-bold ${accent}`}>{value}</p>
      {subtitle && <p className="mt-1 text-[11px] text-zinc-500">{subtitle}</p>}
    </Tag>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [hiddenSeries, setHiddenSeries] = useState({});
  // Sameday "return" badge — orders the customer didn't pick up and are
  // coming back to us. Fetched separately so the heavy /dashboard/stats
  // endpoint doesn't have to traverse the awb_state index.
  const [unpicked, setUnpicked] = useState({ count: 0, platforms: [] });
  // Comenzi finalizate FĂRĂ factură (BAU + Gomag — canalele pe care noi le
  // facturăm). Widget roșu pe Dashboard cu listă și buton „Vezi comanda"
  // care duce direct în Comenzi cu filtru order_id.
  const [noInvoice, setNoInvoice] = useState({ count: 0, items: [] });
  // Erori auto-finalizare RMA-uri BAU: când eMAG întoarce eroare la
  // tranziția 6→7 (recepționat → finalizat). Widget roșu cu link direct
  // la returul cu problema.
  const [rmaErrors, setRmaErrors] = useState({ count: 0, items: [] });
  // Retururi BAU recepționate (status=6) — user cerinta Oct 2026: auto-finalize
  // dezactivat, aceste RMA-uri apar in dashboard ca sa le finalizezi manual.
  const [rmaReceptionate, setRmaReceptionate] = useState({ count: 0, items: [] });
  // Retururi/garanții blocate în status manual (>24h) — la garanție când
  // ajunge la „Verificare Service" sau la retur când e „În depozit — bani
  // de returnat", user-ul trebuie să acționeze. Widget cu link direct.
  const [returnsTodo, setReturnsTodo] = useState({ count: 0, items: [] });
  // Mesaje eMAG deschise care așteaptă răspunsul nostru. Popup roșu în
  // Dashboard cu breakdown per platformă + link direct la Mesaje.
  const [unansweredMsgs, setUnansweredMsgs] = useState({ total: 0, items: [], per_platform: [] });
  // Ramburs neîncasat > 10 zile (Gomag Bragus + Manual Bragus). Banner
  // roșu în Dashboard cu primele 20 comenzi.
  const [codUnpaid, setCodUnpaid] = useState({ count: 0, items: [] });
  // Facturi Financiar neplătite (unpaid + partial). Nu dispar din dashboard
  // decât când sunt plătite integral.
  const [unpaidBills, setUnpaidBills] = useState({ count: 0, items: [], totals_by_currency: {} });
  // Online orders (Netopia) neincasate > 7 zile — user request 2026-09-02.
  const [onlineUnpaid, setOnlineUnpaid] = useState({ count: 0, items: [] });
  // AWB-uri emise > 5 zile fără livrare confirmată — user request 2026-08-28.
  // Refresh polls Sameday inline pentru AWB-urile cu state stale > 12h.
  const [stuckAwbs, setStuckAwbs] = useState({ count: 0, items: [] });

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/orders/stats/cod-unpaid`)
      .then((r) => { if (m) setCodUnpaid({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/orders/stats/online-unpaid`)
      .then((r) => { if (m) setOnlineUnpaid({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    // Endpoint pollează Sameday inline pentru AWB-urile cu state stale;
    // durează 5-15s prima dată, apoi <1s până când cache-ul de 12h expiră.
    const load = () => axios.get(`${API}/dashboard/stuck-awbs?days=5&refresh=true`, { timeout: 90000 })
      .then((r) => { if (m) setStuckAwbs({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    // Refresh every 10 min (cheaper — cache-ul absoarbe apelurile intermediare)
    const id = setInterval(load, 600_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  // Comenzi în proces de întoarcere (awb_state=return) — user request 2026-09-01
  const [returningOrders, setReturningOrders] = useState({ count: 0, items: [] });
  const [mainOfferViolations, setMainOfferViolations] = useState({ count: 0 });
  // Cereri retururi/garantii/schimb/livrare cu status=pending — user request Feb 2026
  const [pendingReturns, setPendingReturns] = useState({ count: 0, counts_by_type: {}, items: [] });
  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/returns/dashboard/pending`, { timeout: 20000 })
      .then((r) => { if (m) setPendingReturns({
        count: r.data?.count || 0,
        counts_by_type: r.data?.counts_by_type || {},
        items: r.data?.items || [],
      }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 300_000);
    return () => { m = false; clearInterval(id); };
  }, []);
  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/main-offer-violations`, { timeout: 20000 })
      .then((r) => { if (m) setMainOfferViolations({ count: r.data?.count || 0, by_platform: r.data?.by_platform || {} }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 300_000); // 5 min
    return () => { m = false; clearInterval(id); };
  }, []);
  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/dashboard/returning-orders`, { timeout: 30000 })
      .then((r) => { if (m) setReturningOrders({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 300_000); // 5 min
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/financiar/facturi/unpaid`)
      .then((r) => {
        if (!m) return;
        // Feb 2026 (bugfix): totals_by_currency e returnat de backend dar
        // era descarcat aici → widget-ul afisa doar count, nu si totalurile
        // per moneda pe care le vrea user-ul (LEI/EUR/USD).
        setUnpaidBills({
          count: r.data?.count || 0,
          items: r.data?.items || [],
          totals_by_currency: r.data?.totals_by_currency || {},
        });
      })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  const markBillPaid = async (billId) => {
    try {
      await axios.post(`${API}/financiar/facturi/${billId}/pay`, { payment_status: "paid" });
      setUnpaidBills((prev) => {
        const removed = (prev.items || []).find((x) => x.id === billId);
        const newItems = (prev.items || []).filter((x) => x.id !== billId);
        // Recalc totals_by_currency: scade `outstanding` factura marcata paid.
        const newTotals = { ...(prev.totals_by_currency || {}) };
        if (removed) {
          const cur = (removed.currency || "RON").toUpperCase();
          newTotals[cur] = Math.max(0, (newTotals[cur] || 0) - (removed.outstanding || 0));
          if (newTotals[cur] < 0.01) delete newTotals[cur];
        }
        return { count: Math.max(0, prev.count - 1), items: newItems, totals_by_currency: newTotals };
      });
    } catch (e) { window.alert("Nu am putut marca factura ca plătită"); }
  };

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/orders/stats/unpicked`)
      .then((r) => { if (m) setUnpicked(r.data); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/orders/stats/finalized-no-invoice`)
      .then((r) => { if (m) setNoInvoice(r.data); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/emag-rmas/auto-finalize-errors?limit=20`)
      .then((r) => { if (m) setRmaErrors({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/emag-rmas/receptionate-pending?limit=20`)
      .then((r) => { if (m) setRmaReceptionate({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/returns/todo-today`)
      .then((r) => { if (m) setReturnsTodo({ count: r.data?.count || 0, items: r.data?.items || [] }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 120_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let m = true;
    const load = () => axios.get(`${API}/messages/unanswered?limit=20`)
      .then((r) => { if (m) setUnansweredMsgs({
        total: r.data?.total || 0,
        items: r.data?.items || [],
        per_platform: r.data?.per_platform || [],
      }); })
      .catch(() => { /* silent */ });
    load();
    const id = setInterval(load, 60_000);
    return () => { m = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = (showSpinner = false) => {
      if (showSpinner) setLoading(true);
      axios.get(`${API}/dashboard/stats`)
        .then((r) => { if (mounted) { setData(r.data); setErr(""); } })
        .catch((e) => { if (mounted) setErr(e.response?.data?.detail || e.message); })
        .finally(() => { if (mounted && showSpinner) setLoading(false); });
    };
    load(true);
    // Auto-refresh every 60 seconds so the KPI cards (orders, platforms,
    // unfinished count, etc.) reflect new data without a manual reload.
    const id = setInterval(() => load(false), 60_000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (loading) return <div className="p-8 text-sm text-zinc-500">Se încarcă...</div>;
  if (err) return <div className="m-4 rounded-md border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300">{err}</div>;
  if (!data) return null;

  const fmt = (n) => new Intl.NumberFormat("ro-RO").format(n);
  const fmtMoney = (n) => new Intl.NumberFormat("ro-RO", { maximumFractionDigits: 0 }).format(n) + " RON";

  const toggleSeries = (k) => setHiddenSeries((h) => ({ ...h, [k]: !h[k] }));

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-zinc-50">Dashboard</h1>
        <p className="mt-0.5 text-xs text-zinc-500">Privire de ansamblu — actualizat live</p>
      </div>

      {/* Row 1: top KPIs */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testId="kpi-orders" icon={ShoppingBag} label="Comenzi"
          value={
            <div className="space-y-2">
              {/* TOP: total THIS month vs total LAST month (full months) */}
              <div className="flex items-baseline gap-2">
                <span data-testid="kpi-orders-this-month" className="text-2xl">{fmt(data.totals.orders_this_month_total || 0)}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-500">luna asta</span>
              </div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-zinc-500">
                  vs <span className="text-zinc-300">{fmt(data.totals.orders_prev_month_total || 0)}</span> luna trecută
                </span>
                {(data.totals.orders_month_delta_pct || 0) >= 0 ? (
                  <span data-testid="kpi-orders-delta" className="inline-flex items-center gap-1 font-medium text-emerald-400">
                    <TrendingUp className="h-3 w-3" />+{data.totals.orders_month_delta_pct}%
                  </span>
                ) : (
                  <span data-testid="kpi-orders-delta" className="inline-flex items-center gap-1 font-medium text-rose-400">
                    <TrendingDown className="h-3 w-3" />{data.totals.orders_month_delta_pct}%
                  </span>
                )}
              </div>
              {/* BOTTOM: same-period comparison (1..today vs 1..today prev month) */}
              <div className="border-t border-zinc-800 pt-2">
                <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-zinc-500">
                  <span>Aceeași perioadă</span>
                  {(data.totals.orders_same_period_delta_pct || 0) >= 0 ? (
                    <span data-testid="kpi-orders-sp-delta" className="inline-flex items-center gap-1 normal-case tracking-normal font-medium text-emerald-400">
                      <TrendingUp className="h-3 w-3" />+{data.totals.orders_same_period_delta_pct}%
                    </span>
                  ) : (
                    <span data-testid="kpi-orders-sp-delta" className="inline-flex items-center gap-1 normal-case tracking-normal font-medium text-rose-400">
                      <TrendingDown className="h-3 w-3" />{data.totals.orders_same_period_delta_pct}%
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between text-[11px]">
                  <div>
                    <div className="text-zinc-500">Luna trecută</div>
                    <div data-testid="kpi-orders-prev" className="font-semibold text-zinc-300">{fmt(data.totals.orders_prev_same_period || 0)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-zinc-500">Luna asta</div>
                    <div data-testid="kpi-orders-curr" className="font-semibold text-emerald-300">{fmt(data.totals.orders_this_month_total || 0)}</div>
                  </div>
                </div>
              </div>
            </div>
          }
          accent="text-orange-300" />
        <KpiCard testId="kpi-platforms" icon={Store} label="Platforme"
          value={fmt(data.totals.platforms_active)}
          subtitle="Conturi active (eMAG + Gomag + Trendyol + telefon)"
          accent="text-sky-300" />
        <KpiCard testId="kpi-products" icon={Package} label="Produse active"
          value={fmt(data.totals.products_active)}
          subtitle="Nearhivate"
          accent="text-violet-300" />
        {/* [MUTAT Feb 2026] KPI „Comenzi nefinalizate" mutat in pagina Comenzi
            lângă „Comenzi noi" (aceeași logică deep-link, filtru identic). */}
      </div>

      {/* Row 1b: [ELIMINAT Feb 2026] KPI-ul simplu "Comenzi neridicate de client"
          a fost sters la cererea userului. Duplica widget-ul de jos "Comenzi
          în proces de întoarcere (nelivrate)" care e mai informativ (lista
          + buton Primit + Vezi comanda). */}

      {/* Row 1c: Comenzi finalizate FĂRĂ factură — canalele BAU + Gomag pe
          care noi le facturăm. Widget roșu prominent cu listă și buton
          „Vezi comandă" per rând. */}
      {noInvoice.count > 0 && (
        <div className="rounded-2xl border-2 border-rose-500/50 bg-rose-950/30 p-4 shadow-lg" data-testid="finalized-no-invoice-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-rose-400" />
              <div>
                <p className="text-sm font-bold text-rose-200">Comenzi finalizate fără factură</p>
                <p className="text-[11px] text-rose-300/80">
                  {noInvoice.count} {noInvoice.count === 1 ? "comandă" : "comenzi"} · începând cu 16 iul 2026 · trebuie să generezi factura
                </p>
              </div>
            </div>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {noInvoice.items.map((it) => (
              <div key={`${it.integration_key}:${it.order_id}`} className="flex items-center justify-between rounded-lg border border-rose-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-rose-200">
                      {it.integration_key.replace("emag_", "").replace("_", " ").toUpperCase()}
                    </span>
                    <span className="truncate text-zinc-200">{it.customer_name || "—"}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                    <span>#{it.order_id}</span>
                    <span>·</span>
                    <span>{it.date}</span>
                    {it.total > 0 && (<><span>·</span><span className="tabular-nums">{Number(it.total).toFixed(2)} RON</span></>)}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/orders?order_id=${encodeURIComponent(it.order_id)}&days=365`)}
                  data-testid={`view-order-${it.order_id}`}
                  className="ml-3 shrink-0 rounded-md bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-rose-500"
                >
                  Vezi comanda →
                </button>
              </div>
            ))}
            {noInvoice.count > noInvoice.items.length && (
              <button
                onClick={() => navigate(`/orders?statuses=4&days=30&invoice_state=missing`)}
                className="w-full rounded-lg border border-rose-800/40 bg-rose-900/20 py-2 text-[11px] font-medium text-rose-200 hover:bg-rose-900/40"
              >
                + încă {noInvoice.count - noInvoice.items.length} comenzi · Vezi toate →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Row 1c.3.b: Financiar — facturi neplătite / achitate parțial. */}
      {unpaidBills.count > 0 && (
        <div className="rounded-2xl border-2 border-amber-500/50 bg-amber-950/25 p-4 shadow-lg" data-testid="unpaid-bills-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Receipt className="h-5 w-5 shrink-0 text-amber-300" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-100">Facturi neplătite</p>
                <p className="text-[11px] text-amber-300/80">
                  {unpaidBills.count} {unpaidBills.count === 1 ? "factură" : "facturi"} · nu dispar din listă până la plata integrală
                </p>
              </div>
            </div>
            {/* Total de plată pe monedă — user request Feb 2026 (repoziționat
                pe același rând cu titlul, ca să fie vizibil in bara compactă). */}
            <div className="flex flex-wrap items-center gap-2">
              {unpaidBills.totals_by_currency && Object.keys(unpaidBills.totals_by_currency).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="unpaid-totals-by-currency">
                  {["RON", "EUR", "USD"].filter((c) => (unpaidBills.totals_by_currency[c] || 0) > 0).map((c) => {
                    const sym = c === "EUR" ? "€" : c === "USD" ? "$" : "lei";
                    return (
                      <span key={c}
                        data-testid={`unpaid-total-${c}`}
                        className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/15 px-2.5 py-1 text-[11px] font-semibold text-amber-200">
                        Total {c}: <span className="tabular-nums text-amber-50">{unpaidBills.totals_by_currency[c].toFixed(2)} {sym}</span>
                      </span>
                    );
                  })}
                </div>
              )}
              <button
                onClick={() => navigate("/financiar")}
                className="rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-500"
                data-testid="unpaid-bills-view-all"
              >
                Deschide Financiar →
              </button>
            </div>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {unpaidBills.items.map((b) => {
              const cur = b.currency || "RON";
              const isPartial = (b.payment_status || "unpaid") === "partial";
              return (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-amber-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isPartial ? (
                        <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-100">PARȚIAL</span>
                      ) : (
                        <span className="rounded bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-rose-100">NEPLĂTITĂ</span>
                      )}
                      {b.numar_factura && <span className="font-mono text-[11px] text-zinc-300">{b.numar_factura}</span>}
                      <span className="truncate text-zinc-200">{b.nume || "—"}</span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-zinc-500">
                      <span>{b.data}</span>
                      {b.days_to_due !== null && b.days_to_due !== undefined && (() => {
                        const d = b.days_to_due;
                        let cls = "text-zinc-400";
                        let label = "";
                        if (d < 0) { cls = "font-bold text-rose-400"; label = `Depășită cu ${Math.abs(d)} ${Math.abs(d) === 1 ? "zi" : "zile"}`; }
                        else if (d === 0) { cls = "font-bold text-rose-300"; label = "Scadență AZI"; }
                        else if (d <= 3) { cls = "font-semibold text-amber-300"; label = `Scadență în ${d} ${d === 1 ? "zi" : "zile"}`; }
                        else if (d <= 7) { cls = "font-medium text-amber-200"; label = `Scadență în ${d} zile`; }
                        else { cls = "text-emerald-300"; label = `Scadență în ${d} zile`; }
                        return <><span>·</span><span className={cls}>{label}</span></>;
                      })()}
                      <span>·</span>
                      <span className="tabular-nums text-zinc-300">Total: {Number(b.valoare_totala || 0).toFixed(2)} {cur}</span>
                      {isPartial && (
                        <>
                          <span>·</span>
                          <span className="tabular-nums text-amber-200">Plătit: {Number(b.paid_amount || 0).toFixed(2)} {cur}</span>
                          <span>·</span>
                          <span className="tabular-nums font-semibold text-amber-300">Rest: {Number(b.outstanding || 0).toFixed(2)} {cur}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => markBillPaid(b.id)}
                    data-testid={`unpaid-bills-pay-${b.id}`}
                    className="ml-3 shrink-0 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-500"
                    title="Marchează factura ca plătită integral"
                  >
                    ✓ Factura plătită
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Row 1c.4: Ramburs neîncasat > 10 zile (Gomag Bragus + Manual Bragus). */}
      {codUnpaid.count > 0 && (
        <div className="rounded-2xl border-2 border-purple-500/50 bg-purple-950/25 p-4 shadow-lg" data-testid="cod-unpaid-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-purple-300" />
              <div>
                <p className="text-sm font-bold text-purple-100">Ramburs neîncasat &gt; 10 zile</p>
                <p className="text-[11px] text-purple-300/80">
                  {codUnpaid.count} {codUnpaid.count === 1 ? "comandă finalizată" : "comenzi finalizate"} · Sameday nu a marcat rambursul ca transferat
                </p>
              </div>
            </div>
            <button
              onClick={async () => {
                try {
                  await axios.post(`${API}/emag-vendor-rpa/cod-refunds/sync`);
                  // Refresh banner shortly after sync
                  setTimeout(async () => {
                    try {
                      const r = await axios.get(`${API}/orders/stats/cod-unpaid`);
                      setCodUnpaid({ count: r.data?.count || 0, items: r.data?.items || [] });
                    } catch { /* silent */ }
                  }, 800);
                } catch {
                  window.alert("Sync eșuat — vezi log-urile backend");
                }
              }}
              data-testid="cod-unpaid-sync-now"
              className="rounded-md border border-purple-500/40 bg-purple-500/10 px-2.5 py-1 text-[11px] font-semibold text-purple-200 hover:bg-purple-500/20"
              title="Verifică Sameday acum pentru toate comenzile neîncasate"
            >
              ⟲ Verifică Sameday
            </button>
            <button
              onClick={() => navigate(`/orders?cod_refund=unpaid_over_10d`)}
              className="rounded-md bg-purple-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-purple-500"
              data-testid="cod-unpaid-view-all"
            >
              Vezi toate →
            </button>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {codUnpaid.items.map((it) => (
              <div key={`${it.integration_key}:${it.order_id}`} className="flex items-center justify-between rounded-lg border border-purple-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-purple-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-purple-100">
                      {it.integration_key === "manual_bragus" ? "MANUAL" : "GOMAG"}
                    </span>
                    <span className="truncate text-zinc-200">{it.customer_name || "—"}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                    <span>#{it.order_id}</span>
                    {it.awb_number && (<><span>·</span><span className="font-mono">{it.awb_number}</span></>)}
                    {it.reference_date && (<><span>·</span><span>livrat {new Date(it.reference_date).toLocaleDateString("ro-RO")}</span></>)}
                    {it.total > 0 && (<><span>·</span><span className="tabular-nums text-purple-200">{Number(it.total).toFixed(2)} RON</span></>)}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/orders?order_id=${encodeURIComponent(it.order_id)}`)}
                  data-testid={`cod-unpaid-view-${it.order_id}`}
                  className="ml-3 shrink-0 rounded-md bg-purple-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-purple-500"
                >
                  Vezi comanda →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Row 1c.4a2: Online orders (Netopia) neîncasate > 7 zile — user
          request 2026-09-02. Sursă: `orders.netopia_paid != true` cu
          `payment_mode_id in [2,3]`, status=4, delivered/finalized > 7d. */}
      {onlineUnpaid.count > 0 && (
        <div className="rounded-2xl border-2 border-sky-500/50 bg-sky-950/25 p-4 shadow-lg" data-testid="online-unpaid-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-sky-300" />
              <div>
                <p className="text-sm font-bold text-sky-100">Online neîncasat &gt; 7 zile</p>
                <p className="text-[11px] text-sky-300/80">
                  {onlineUnpaid.count} {onlineUnpaid.count === 1 ? "comandă online finalizată" : "comenzi online finalizate"} · Netopia nu a decontat încă
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate(`/financiar`)}
              className="rounded-md bg-sky-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-sky-500"
              data-testid="online-unpaid-view-all"
            >
              Vezi în Financiar →
            </button>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {onlineUnpaid.items.map((it) => (
              <div key={`${it.integration_key}:${it.order_id}`} className="flex items-center justify-between rounded-lg border border-sky-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-sky-100">
                      {it.integration_key === "manual_bragus" ? "MANUAL" : "GOMAG"}
                    </span>
                    <span className="truncate text-zinc-200">{it.customer_name || "—"}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                    <span>#{it.order_id}</span>
                    {it.reference_date && (<><span>·</span><span>livrat {new Date(it.reference_date).toLocaleDateString("ro-RO")}</span></>)}
                    {it.total > 0 && (<><span>·</span><span className="tabular-nums text-sky-200">{Number(it.total).toFixed(2)} RON</span></>)}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/orders?order_id=${encodeURIComponent(it.order_id)}`)}
                  data-testid={`online-unpaid-view-${it.order_id}`}
                  className="ml-3 shrink-0 rounded-md bg-sky-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-sky-500"
                >
                  Vezi comanda →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Row 1c.4b: AWB-uri emise > 5 zile fără livrare confirmată — user request 2026-08-28.
          Sameday-format AWBs sunt refresh-uite inline din Sameday API; eMAG BAU
          courier AWBs (4EMG…) sunt afișate cu state „unknown" pentru că
          Sameday nu le recunoaște. Click pe AWB → deschide tracking Sameday. */}
      {stuckAwbs.count > 0 && (
        <div className="rounded-2xl border-2 border-sky-500/50 bg-sky-950/25 p-4 shadow-lg" data-testid="stuck-awbs-card">
          <div className="mb-3 flex items-center gap-2">
            <Package className="h-5 w-5 text-sky-300" />
            <div>
              <p className="text-sm font-bold text-sky-100">AWB-uri emise &gt; 5 zile fără livrare confirmată</p>
              <p className="text-[11px] text-sky-300/80">
                {stuckAwbs.count} {stuckAwbs.count === 1 ? "AWB în tranzit" : "AWB-uri în tranzit"} · click pe AWB pentru tracking Sameday
              </p>
            </div>
          </div>
          <div className="max-h-80 space-y-1.5 overflow-y-auto">
            {stuckAwbs.items.map((it) => {
              const isSameday = it.carrier === "sameday";
              const stateBadge = it.awb_state === "on_route"
                ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
                : it.awb_state === "ready"
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
                  : it.awb_state === "return"
                    ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                    : "border-zinc-700 bg-zinc-800 text-zinc-400";
              return (
                <div key={it.awb_number} className="flex items-center justify-between rounded-lg border border-sky-900/40 bg-zinc-950/60 px-3 py-2 text-xs" data-testid={`stuck-awb-${it.awb_number}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded bg-sky-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-sky-100">
                        {it.integration_key === "manual_bragus" ? "MANUAL"
                          : it.integration_key === "gomag_bragus" ? "GOMAG"
                          : it.integration_key === "emag_ro_bau" ? "eMAG RO"
                          : (it.integration_key || "?").toUpperCase()}
                      </span>
                      <a
                        href={it.tracking_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="font-mono text-sky-200 hover:text-sky-100 hover:underline"
                        title={isSameday ? "Deschide tracking Sameday" : "AWB eMAG (courier propriu)"}
                        data-testid={`stuck-awb-tracking-${it.awb_number}`}
                      >
                        {it.awb_number}
                      </a>
                      <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${stateBadge}`} data-testid={`stuck-awb-state-${it.awb_number}`} title={it.awb_state_raw ? `Sameday: ${it.awb_state_raw}` : ""}>
                        {it.awb_state_label}
                      </span>
                      <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-200" title="Zile de la emiterea AWB">
                        {it.days_in_transit}z
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-zinc-500">
                      <span className="truncate text-zinc-300">{it.customer_name}</span>
                      {it.order_number && (<><span>·</span><span>#{it.order_number}</span></>)}
                      {it.delivery_mode && (<><span>·</span><span>{it.delivery_mode}</span></>)}
                      {it.awb_state_raw && (
                        <>
                          <span>·</span>
                          <span className="text-sky-300/90" data-testid={`stuck-awb-state-raw-${it.awb_number}`}>
                            Status: <span className="text-sky-100 font-medium">{it.awb_state_raw}</span>
                          </span>
                        </>
                      )}
                      {it.cash_on_delivery > 0 && (
                        <>
                          <span>·</span>
                          <span className="tabular-nums text-emerald-300">COD {Number(it.cash_on_delivery).toFixed(2)}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="ml-3 flex shrink-0 items-center gap-1.5">
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Ignori AWB ${it.awb_number}? Nu va mai apărea în această listă.`)) return;
                        try {
                          await axios.post(`${API}/dashboard/stuck-awbs/${encodeURIComponent(it.awb_number)}/ignore`);
                          setStuckAwbs((prev) => ({
                            count: Math.max(0, (prev?.count || 0) - 1),
                            items: (prev?.items || []).filter((x) => x.awb_number !== it.awb_number),
                          }));
                          toast.success(`AWB ${it.awb_number} ignorat`);
                        } catch (e) {
                          toast.error("Nu s-a putut ignora AWB-ul", { description: e?.response?.data?.detail || e.message });
                        }
                      }}
                      data-testid={`stuck-awb-ignore-${it.awb_number}`}
                      title="Ignoră acest AWB — nu-l mai afișa în listă"
                      className="rounded-md border border-zinc-700 bg-zinc-800/80 px-2.5 py-1 text-[11px] font-semibold text-zinc-300 hover:border-rose-500/50 hover:bg-rose-500/10 hover:text-rose-200"
                    >
                      Ignoră
                    </button>
                    <button
                      onClick={() => navigate(`/orders?awb=${encodeURIComponent(it.awb_number)}`)}
                      data-testid={`stuck-awb-open-${it.awb_number}`}
                      className="rounded-md bg-sky-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-sky-500"
                    >
                      Vezi comanda →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Produse Care nu au Oferta Principala — user cerință Feb 2026.
          Un scan orar detectează SKU-urile active cu Buy Button rank = 2. */}
      {mainOfferViolations.count > 0 && (
        <div className="rounded-2xl border-2 border-orange-500/50 bg-orange-950/25 p-4 shadow-lg" data-testid="main-offer-violations-card">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-orange-300" />
              <div>
                <p className="text-sm font-bold text-orange-100">Produse Care nu au Oferta Principala</p>
                <p className="text-[11px] text-orange-300/80">
                  {mainOfferViolations.count} {mainOfferViolations.count === 1 ? "produs" : "produse"} cu poziția 2 · Buy Button preluat de alt seller
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/main-offer-violations")}
              data-testid="main-offer-violations-open"
              className="rounded-md bg-orange-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-orange-500"
            >
              Deschide →
            </button>
          </div>
          {mainOfferViolations.by_platform && (
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(mainOfferViolations.by_platform).map(([k, n]) => (
                <span key={k} className="rounded-full border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[10px] text-orange-100">
                  {k.replace("emag_", "").toUpperCase().replace("_", " ")}: {n}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Comenzi în proces de întoarcere (awb_state=return) —
          user request 2026-09-01. */}
      {returningOrders.count > 0 && (
        <div className="rounded-2xl border-2 border-rose-500/50 bg-rose-950/25 p-4 shadow-lg" data-testid="returning-orders-card">
          <div className="mb-3 flex items-center gap-2">
            <Package className="h-5 w-5 text-rose-300" />
            <div>
              <p className="text-sm font-bold text-rose-100">Comenzi în proces de întoarcere (nelivrate)</p>
              <p className="text-[11px] text-rose-300/80">
                {returningOrders.count} {returningOrders.count === 1 ? "colet se întoarce" : "colete se întorc"} către noi · Gomag + Manual Bragus
              </p>
            </div>
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {returningOrders.items.map((it) => (
              <div key={it.awb_number} className="flex items-center justify-between rounded-lg border border-rose-900/40 bg-zinc-950/60 px-3 py-2 text-xs" data-testid={`returning-order-${it.awb_number}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <a href={`https://sameday.ro/#awb=${it.awb_number}`} target="_blank" rel="noreferrer noopener" className="font-mono font-semibold text-rose-300 hover:text-rose-100 hover:underline" title="Deschide tracking Sameday" data-testid={`returning-order-tracking-${it.awb_number}`}>{it.awb_number}</a>
                    <span className="rounded-full border border-rose-500/40 bg-rose-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-rose-200">În retur</span>
                    <span className="text-[10px] text-rose-400/70">{it.awb_created_at ? new Date(it.awb_created_at).toLocaleDateString("ro-RO") : "—"}</span>
                  </div>
                  <div className="mt-0.5 truncate text-[11px] text-zinc-400">
                    #{it.order_number} · {it.customer_name || "—"} · Total: <strong className="text-zinc-200">{Number(it.total || 0).toFixed(2)} RON</strong>
                  </div>
                </div>
                <div className="ml-3 flex shrink-0 gap-1">
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Marchezi AWB ${it.awb_number} ca „retur livrat la noi"? Va fi scos din widget.`)) return;
                      try {
                        await axios.post(`${API}/dashboard/returning-orders/${encodeURIComponent(it.awb_number)}/dismiss`);
                        setReturningOrders((r) => ({ count: Math.max(0, (r.count || 0) - 1), items: (r.items || []).filter((x) => x.awb_number !== it.awb_number) }));
                      } catch (e) { window.alert("Eroare: " + (e?.response?.data?.detail || e.message)); }
                    }}
                    data-testid={`returning-order-dismiss-${it.awb_number}`}
                    title="Marchează ca returnat la noi (scoate din widget)"
                    className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-[11px] font-semibold text-emerald-200 hover:bg-emerald-500/20"
                  >✓ Primit</button>
                  <button
                    onClick={() => navigate(`/orders?awb=${encodeURIComponent(it.awb_number)}`)}
                    data-testid={`returning-order-open-${it.awb_number}`}
                    className="rounded-md bg-rose-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-rose-500"
                  >Vezi comanda →</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Row 1c.4: Retururi / Garantii / Schimb / Livrare in ASTEPTARE
          — user request Feb 2026. Card portocaliu prominent langa
          "Comenzi in proces de intoarcere". Afiseaza doar cereri cu
          status = pending (necesita actiune imediata, AWB neemis inca). */}
      {pendingReturns.count > 0 && (
        <div className="rounded-2xl border-2 border-amber-500/50 bg-amber-950/25 p-4 shadow-lg" data-testid="pending-returns-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-300" />
              <div>
                <p className="text-sm font-bold text-amber-100">Retururi &amp; Garanții în așteptare</p>
                <p className="text-[11px] text-amber-300/80">
                  {pendingReturns.count} {pendingReturns.count === 1 ? "cerere așteaptă" : "cereri așteaptă"} acțiune (AWB neemis încă)
                  {(() => {
                    const labels = { garantie: "garanție", retur: "retur", schimb: "schimb", livrare: "livrare" };
                    const parts = ["garantie", "retur", "schimb", "livrare"]
                      .map((k) => {
                        const n = pendingReturns.counts_by_type[k] || 0;
                        return n > 0 ? `${n} ${labels[k]}` : null;
                      })
                      .filter(Boolean);
                    return parts.length ? " · " + parts.join(" · ") : "";
                  })()}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/returns?status=pending")}
              data-testid="pending-returns-open-all"
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500"
            >
              Deschide toate →
            </button>
          </div>
          <div className="space-y-1.5 max-h-96 overflow-y-auto">
            {pendingReturns.items.map((it) => {
              const typeColors = {
                garantie: "border-purple-500/40 bg-purple-500/10 text-purple-200",
                retur:    "border-rose-500/40   bg-rose-500/10   text-rose-200",
                schimb:   "border-sky-500/40    bg-sky-500/10    text-sky-200",
                livrare:  "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
              };
              const cls = typeColors[it.type] || typeColors.garantie;
              return (
                <div key={it.id} className="flex items-center justify-between rounded-lg border border-amber-900/40 bg-zinc-950/60 px-3 py-2 text-xs" data-testid={`pending-return-${it.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{it.type_label}</span>
                      <span className="font-mono text-[11px] text-zinc-300">#{it.number}</span>
                      <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200">În așteptare</span>
                      <span className="text-[10px] text-amber-400/70">{it.created_at ? new Date(it.created_at).toLocaleDateString("ro-RO") : "—"}</span>
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-zinc-400">
                      {it.customer_name || "—"} · {it.customer_phone || "—"}
                      {it.order_id ? <> · comanda <strong className="text-zinc-200">#{it.order_id}</strong></> : null}
                      {it.product_name ? <> · <span className="text-zinc-300">{it.product_name}</span></> : null}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/returns/${it.id}`)}
                    data-testid={`pending-return-open-${it.id}`}
                    className="ml-3 shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-500"
                  >Vezi cererea →</button>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Row 1c.5: Mesaje eMAG deschise fără răspuns — popup roșu prominent
          cu breakdown per platformă + listă mesaje și link direct la Mesaje. */}
      {unansweredMsgs.total > 0 && (
        <div className="rounded-2xl border-2 border-amber-500/50 bg-amber-950/20 p-4 shadow-lg" data-testid="unanswered-messages-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-amber-400" />
              <div>
                <p className="text-sm font-bold text-amber-200">Mesaje eMAG fără răspuns</p>
                <p className="text-[11px] text-amber-300/80">
                  {unansweredMsgs.total} {unansweredMsgs.total === 1 ? "mesaj deschis" : "mesaje deschise"} · așteaptă răspunsul tău
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/messages")}
              data-testid="unanswered-open-messages"
              className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500"
            >
              Deschide Mesaje →
            </button>
          </div>
          {/* Breakdown per platformă */}
          {unansweredMsgs.per_platform.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {unansweredMsgs.per_platform.map((p) => (
                <button
                  key={p.key}
                  onClick={() => navigate(`/messages?platform=${encodeURIComponent(p.key)}`)}
                  data-testid={`platform-chip-${p.key}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-medium text-amber-100 hover:bg-amber-500/20"
                >
                  <span>{p.name}</span>
                  <span className="rounded-full bg-amber-500/30 px-1.5 text-[10px] font-bold">{p.count}</span>
                </button>
              ))}
            </div>
          )}
          {/* Listă mesaje */}
          <div className="max-h-64 space-y-1.5 overflow-y-auto">
            {unansweredMsgs.items.map((m) => (
              <div key={m._composite_id} className="flex items-center justify-between rounded-lg border border-amber-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200">
                      {(m._integration_key || "").replace("emag_", "").replace("_", " ").toUpperCase()}
                    </span>
                    <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-400">
                      {m.case_type}
                    </span>
                    <span className="truncate text-zinc-200">{m.subject || `Comanda #${m.order_id || "?"}`}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                    <span>Status: {m.status || m.status_raw || "—"}</span>
                    {m.created_at && (<><span>·</span><span>{String(m.created_at).slice(0, 10)}</span></>)}
                    {m.severity && m.severity !== "Low" && (<><span>·</span><span className="text-rose-300">Severitate: {m.severity}</span></>)}
                  </div>
                </div>
                {m.order_id && (
                  <button
                    onClick={() => navigate(`/messages?order_id=${encodeURIComponent(m.order_id)}`)}
                    data-testid={`open-msg-${m.order_id}`}
                    className="ml-3 shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-500"
                  >
                    Deschide →
                  </button>
                )}
              </div>
            ))}
            {unansweredMsgs.total > unansweredMsgs.items.length && (
              <button
                onClick={() => navigate("/messages")}
                className="w-full rounded-lg border border-amber-800/40 bg-amber-900/20 py-2 text-[11px] font-medium text-amber-200 hover:bg-amber-900/40"
              >
                + încă {unansweredMsgs.total - unansweredMsgs.items.length} mesaje · Vezi toate →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Row 1c-bis: Retururi BAU RECEPTIONATE (status=6) — user cerinta Oct 2026.
          Auto-finalize dezactivat; aceste RMA-uri apar aici cu buton
          „Vezi returul" pentru a le finaliza manual din eMAG. */}
      {rmaReceptionate.count > 0 && (
        <div className="rounded-2xl border-2 border-emerald-500/50 bg-emerald-950/30 p-4 shadow-lg" data-testid="rma-receptionate-pending-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-emerald-400" />
              <div>
                <p className="text-sm font-bold text-emerald-200">Retururi recepționate (BAU) — finalizează manual</p>
                <p className="text-[11px] text-emerald-300/80">
                  {rmaReceptionate.count} {rmaReceptionate.count === 1 ? "retur așteaptă" : "retururi așteaptă"} finalizarea · click pe „Vezi returul" să treci la Finalizat pe eMAG
                </p>
              </div>
            </div>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {rmaReceptionate.items.map((r) => (
              <div key={r._composite_id || r._emag_id || r.emag_id} className="flex items-center justify-between rounded-lg border border-emerald-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-200">
                      {(r._integration_key || "").replace("emag_", "").replace("_", " ").toUpperCase()}
                    </span>
                    <span className="truncate text-zinc-200">{r.customer_name || "—"}</span>
                    {r.order_id && (
                      <span className="text-[10px] text-zinc-500">· #{r.order_id}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                    <span>RMA #{r._emag_id || r.emag_id}</span>
                    {r.date && (
                      <>
                        <span>·</span>
                        <span>{(r.date || "").slice(0, 10)}</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/emag-rmas?rma_id=${encodeURIComponent(r._emag_id || r.emag_id)}`)}
                  data-testid={`view-receptionate-rma-${r._emag_id || r.emag_id}`}
                  className="ml-3 shrink-0 rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-emerald-500"
                >
                  Vezi returul →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Row 1d: RMA-uri BAU cu erori la auto-finalizare (6 → 7). Widget
          galben — deseori cauzat de retururi duplicate direct din eMAG. */}
      {rmaErrors.count > 0 && (
        <div className="rounded-2xl border-2 border-amber-500/50 bg-amber-950/30 p-4 shadow-lg" data-testid="rma-auto-finalize-errors-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-400" />
              <div>
                <p className="text-sm font-bold text-amber-200">Erori finalizare retururi eMAG (BAU)</p>
                <p className="text-[11px] text-amber-300/80">
                  {rmaErrors.count} {rmaErrors.count === 1 ? "retur" : "retururi"} · nu s-au putut auto-finaliza · verifică manual pe eMAG (deseori duplicate)
                </p>
              </div>
            </div>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {rmaErrors.items.map((r) => (
              <div key={r._composite_id || r._emag_id} className="flex items-center justify-between rounded-lg border border-amber-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-200">
                      {(r._integration_key || "").replace("emag_", "").replace("_", " ").toUpperCase()}
                    </span>
                    <span className="truncate text-zinc-200">{r.customer_name || "—"}</span>
                    {r.order_id && (
                      <span className="text-[10px] text-zinc-500">· #{r.order_id}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                    <span>RMA #{r._emag_id || r.emag_id}</span>
                    <span>·</span>
                    <span title={r._auto_finalize_error} className="truncate text-amber-400/80">
                      {r._auto_finalize_error}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => navigate(`/emag-rmas?rma_id=${encodeURIComponent(r._emag_id || r.emag_id)}`)}
                  data-testid={`view-rma-${r._emag_id || r.emag_id}`}
                  className="ml-3 shrink-0 rounded-md bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-amber-500"
                >
                  Vezi returul →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Row 1e: Garanții/Retururi de rezolvat azi — status blocat de >24h
          la „Verificare Service" (garanție) sau „În depozit — bani de
          returnat" (retur). Widget indigo cu link direct la cerere. */}
      {returnsTodo.count > 0 && (
        <div className="rounded-2xl border-2 border-indigo-500/50 bg-indigo-950/30 p-4 shadow-lg" data-testid="returns-todo-today-card">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-indigo-400" />
              <div>
                <p className="text-sm font-bold text-indigo-200">Retururi / Garanții de rezolvat azi</p>
                <p className="text-[11px] text-indigo-300/80">
                  {returnsTodo.count} {returnsTodo.count === 1 ? "cerere" : "cereri"} · blocate în verificare de peste 24h
                </p>
              </div>
            </div>
          </div>
          <div className="max-h-72 space-y-1.5 overflow-y-auto">
            {returnsTodo.items.map((r) => {
              const isGarantie = r.request_type === "garantie";
              const statusLabel = isGarantie ? "Verificare Service" : "În depozit · Bani de returnat";
              const typeLabel = isGarantie ? "GARANȚIE" : "RETUR";
              return (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-indigo-900/40 bg-zinc-950/60 px-3 py-2 text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${isGarantie ? "bg-orange-500/20 text-orange-200" : "bg-sky-500/20 text-sky-200"}`}>
                        {typeLabel}
                      </span>
                      <span className="truncate text-zinc-200">{(r.customer || {}).name || "—"}</span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-[10px] text-zinc-500">
                      <span className="font-mono">#{r.id?.slice(0, 8) || "?"}</span>
                      {r.order_id && (<><span>·</span><span>comandă #{r.order_id}</span></>)}
                      <span>·</span>
                      <span className="text-indigo-400/80">{statusLabel}</span>
                      {r.updated_at && (
                        <>
                          <span>·</span>
                          <span>din {new Date(r.updated_at).toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit" })}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/returns/${r.id}`)}
                    data-testid={`view-return-${r.id}`}
                    className="ml-3 shrink-0 rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-indigo-500"
                  >
                    Vezi cererea →
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Row 2: revenue + profit comparison */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-300">Luna curentă · Încasări</p>
            <TrendingUp className="h-4 w-4 text-emerald-300" />
          </div>
          <p data-testid="kpi-revenue-current" className="mt-2 text-3xl font-bold text-emerald-200">{fmtMoney(data.revenue.current_month)}</p>
          {data.revenue.previous_month_same_period != null && (
            <div className="mt-2 flex items-center gap-2 text-[11px]">
              <span className="text-zinc-500">vs aceeași perioadă luna trecută:</span>
              <span className="font-mono text-zinc-300" data-testid="kpi-revenue-prev-same-period">{fmtMoney(data.revenue.previous_month_same_period)}</span>
              {(data.revenue.delta_pct_same_period ?? 0) >= 0
                ? <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 font-medium text-emerald-300"><TrendingUp className="h-3 w-3" />+{data.revenue.delta_pct_same_period}%</span>
                : <span className="inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 font-medium text-rose-300"><TrendingDown className="h-3 w-3" />{data.revenue.delta_pct_same_period}%</span>}
            </div>
          )}
          <p className="mt-1 text-[11px] text-zinc-500">Finalizate + În procesare + Nouă · BG/HU convertite în RON (BNR)</p>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">Luna trecută · Încasări</p>
            {data.revenue.delta_pct >= 0
              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400"><TrendingUp className="h-3 w-3" />+{data.revenue.delta_pct}%</span>
              : <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-400"><TrendingDown className="h-3 w-3" />{data.revenue.delta_pct}%</span>}
          </div>
          <p data-testid="kpi-revenue-previous" className="mt-2 text-3xl font-bold text-zinc-300">{fmtMoney(data.revenue.previous_month)}</p>
          <p className="mt-1 text-[11px] text-zinc-500">Comparativ cu luna anterioară (total)</p>
        </div>
      </div>

      {/* Row 2b: profit comparison (RON, finalised only) */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-violet-500/30 bg-violet-500/5 p-5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-medium uppercase tracking-wider text-violet-300">Luna curentă · Profit</p>
            <TrendingUp className="h-4 w-4 text-violet-300" />
          </div>
          <p data-testid="kpi-profit-current" className="mt-2 text-3xl font-bold text-violet-200">{fmtMoney(data.revenue.current_month_profit)}</p>
          {data.revenue.previous_month_profit_same_period != null && (
            <div className="mt-2 flex items-center gap-2 text-[11px]">
              <span className="text-zinc-500">vs aceeași perioadă luna trecută:</span>
              <span className="font-mono text-zinc-300" data-testid="kpi-profit-prev-same-period">{fmtMoney(data.revenue.previous_month_profit_same_period)}</span>
              {(data.revenue.profit_delta_pct_same_period ?? 0) >= 0
                ? <span className="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 font-medium text-emerald-300"><TrendingUp className="h-3 w-3" />+{data.revenue.profit_delta_pct_same_period}%</span>
                : <span className="inline-flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 font-medium text-rose-300"><TrendingDown className="h-3 w-3" />{data.revenue.profit_delta_pct_same_period}%</span>}
            </div>
          )}
          <p className="mt-1 text-[11px] text-zinc-500">Doar finalizate · BG/HU convertite în RON (BNR)</p>
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-5">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">Luna trecută · Profit</p>
            {data.revenue.profit_delta_pct >= 0
              ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400"><TrendingUp className="h-3 w-3" />+{data.revenue.profit_delta_pct}%</span>
              : <span className="inline-flex items-center gap-1 text-xs font-medium text-rose-400"><TrendingDown className="h-3 w-3" />{data.revenue.profit_delta_pct}%</span>}
          </div>
          <p data-testid="kpi-profit-previous" className="mt-2 text-3xl font-bold text-zinc-300">{fmtMoney(data.revenue.previous_month_profit)}</p>
          <p className="mt-1 text-[11px] text-zinc-500">Comparativ cu luna anterioară (total)</p>
        </div>
      </div>

      {/* Row 3: charts */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Orders chart — finalizate / noi / anulate */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4 lg:col-span-2">
          <p className="mb-3 text-xs font-medium text-zinc-300">Comenzi (ultimele 8 zile)</p>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {["finalizate", "noi", "anulate"].map((k) => (
              <button key={k} onClick={() => toggleSeries(k)}
                data-testid={`chart-toggle-${k}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider transition ${hiddenSeries[k] ? "border-zinc-800 bg-zinc-900 text-zinc-500" : "border-zinc-700 bg-zinc-900 text-zinc-200"}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: hiddenSeries[k] ? "#52525b" : STATUS_COLORS[k] }} />
                {k}
              </button>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.daily_chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="day" stroke="#71717a" fontSize={10} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#71717a" fontSize={10} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, fontSize: 11 }} />
              {!hiddenSeries.finalizate && <Bar dataKey="finalizate" stackId="a" fill={STATUS_COLORS.finalizate} radius={[0, 0, 0, 0]} />}
              {!hiddenSeries.noi && <Bar dataKey="noi" stackId="a" fill={STATUS_COLORS.noi} radius={[0, 0, 0, 0]} />}
              {!hiddenSeries.anulate && <Bar dataKey="anulate" stackId="a" fill={STATUS_COLORS.anulate} radius={[4, 4, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Platform donut */}
        <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
          <p className="mb-3 text-xs font-medium text-zinc-300">Distribuție pe platforme (30 zile)</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, fontSize: 11 }} />
              <Pie data={data.platform_pie} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={2}>
                {data.platform_pie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 10, color: "#a1a1aa" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 4: Încasări + Profit per zi — 2 culori (doar finalizate) */}
      <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70 p-4">
        <p className="mb-1 text-xs font-medium text-zinc-300">Încasări &amp; Profit pe zi · RON (ultimele 8 zile)</p>
        <p className="mb-3 text-[10px] text-zinc-500">Doar comenzile finalizate · BG/HU convertite cu cursul BNR</p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.daily_chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis dataKey="day" stroke="#71717a" fontSize={10} tickFormatter={(v) => v.slice(5)} />
            <YAxis stroke="#71717a" fontSize={10} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
            <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 6, fontSize: 11 }} formatter={(v, n) => [`${fmt(Math.round(v))} RON`, n]} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#a1a1aa" }} />
            <Bar dataKey="rev_finalizate" name="Încasări totale" fill="#10b981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="profit" name="Profit" fill="#a78bfa" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
